import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import http from '../http';
import C from '../colors';
import WithdrawalFeeNotice from './WithdrawalFeeNotice';
import WithdrawalLimitNotice from './WithdrawalLimitNotice';

const POLL_INTERVAL_MS = 2000;

export const WithDrawDialog = observer(({ open, onClose, onSuccess }) => {
  const [qrCode, setQrCode] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [qrStatus, setQrStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setQrCode('');
    setSessionId('');
    setQrStatus('pending');
    setLoadError('');
    setStatusError('');
    setLoading(true);
    http.post('/user/withdrawQrCode', {}).then(res => {
      if (cancelled) return;
      if (res.status === 200 && res.data?.imageData && res.data?.sessionId) {
        setQrCode(res.data.imageData);
        setSessionId(res.data.sessionId);
      } else {
        setLoadError('二维码生成失败，请重试。');
      }
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setLoadError('二维码生成失败，请检查网络后重试。');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, generation]);

  useEffect(() => {
    if (!open || !sessionId || qrStatus !== 'pending') return;
    let cancelled = false;
    let checking = false;

    const checkStatus = async () => {
      if (checking) return;
      checking = true;
      try {
        const response = await http.get(`/user/withdrawQrCode/${encodeURIComponent(sessionId)}/status`);
        if (cancelled) return;
        const nextStatus = response.data?.status;
        setStatusError('');
        if (nextStatus === 'consumed' || nextStatus === 'expired') {
          setQrStatus(nextStatus);
          if (nextStatus === 'consumed') onSuccess?.();
        }
      } catch (error) {
        if (!cancelled) setStatusError('二维码状态暂时无法更新，正在自动重试。');
      } finally {
        checking = false;
      }
    };

    const timer = window.setInterval(checkStatus, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, sessionId, qrStatus, onSuccess]);

  const regenerate = () => setGeneration(value => value + 1);

  if (!open) return null;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1300,
      background: 'rgba(26,20,23,.45)',
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.paper, borderRadius: 14, border: `1px solid ${C.hair}`,
        width: '100%', maxWidth: 460,
        boxShadow: '0 20px 50px -12px rgba(0,0,0,.25)',
        padding: '28px 28px 24px', textAlign: 'center',
      }}>
        <h3 style={{ margin: '0 0 20px', fontFamily: C.serif, fontSize: 22, fontWeight: 400, color: C.ink }}>微信提现二维码</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
          {loading && <span style={{ fontSize: 14, color: C.ink3 }}>加载中…</span>}
          {!loading && qrCode && (
            <div style={{ position: 'relative', width: 200, height: 200 }}>
              <img src={`data:image/png;base64,${qrCode}`} alt="提现二维码" style={{ width: 200, height: 200, borderRadius: 8, opacity: qrStatus === 'pending' ? 1 : 0.18 }}/>
              {qrStatus !== 'pending' && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: C.sans, fontSize: 14, fontWeight: 600, color: C.ink }}>
                  {qrStatus === 'consumed' ? '已使用' : '已过期'}
                </div>
              )}
            </div>
          )}
          {!loading && loadError && <span data-testid="withdraw-qr-error" style={{ maxWidth: 240, fontSize: 13, lineHeight: 1.6, color: '#c2412e' }}>{loadError}</span>}
        </div>
        <div data-testid="withdraw-qr-status" style={{ margin: '12px 0 14px', fontSize: 12.5, lineHeight: 1.55, color: qrStatus === 'consumed' ? '#27734f' : qrStatus === 'expired' ? '#c2412e' : C.ink3 }}>
          {loading && '正在生成二维码…'}
          {!loading && !loadError && qrStatus === 'pending' && '等待微信扫码 · 二维码仅可使用一次'}
          {!loading && !loadError && qrStatus === 'consumed' && '二维码已使用，请在微信中继续提现'}
          {!loading && !loadError && qrStatus === 'expired' && '二维码已过期，请重新生成'}
          {!loading && loadError && (
            <button onClick={regenerate} style={{ padding: '8px 16px', borderRadius: 9, border: 0, background: C.ink, color: '#fff', cursor: 'pointer' }}>重新生成</button>
          )}
        </div>
        {statusError && qrStatus === 'pending' && <div data-testid="withdraw-qr-status-error" style={{ margin: '-7px 0 12px', fontSize: 11.5, color: C.ink3 }}>{statusError}</div>}
        {qrStatus === 'expired' && (
          <button onClick={regenerate} style={{ margin: '-4px 0 14px', padding: '8px 16px', borderRadius: 9, border: 0, background: C.ink, color: '#fff', cursor: 'pointer' }}>重新生成二维码</button>
        )}
        <WithdrawalFeeNotice compact />
        <div style={{ marginTop: 10 }}><WithdrawalLimitNotice compact /></div>
        <button onClick={onClose} style={{ marginTop: 18, padding: '9px 24px', borderRadius: 9, border: `1px solid ${C.hair2}`, background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 13, cursor: 'pointer' }}>
          关闭
        </button>
      </div>
    </div>
  );
});

export default WithDrawDialog;
